import { useEffect, useState } from "react";
import { api, fmtErr } from "../../lib/api";
import { Warning, FlagBanner } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ScmLeadTimes() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/scm/lead-times").then(({ data }) => setItems(data)).catch((e) => toast.error(fmtErr(e)));
  }, []);

  const dated = items.filter((i) => i.expected_date);
  const dates = items.flatMap((i) => [i.created_at?.slice(0, 10), i.expected_date, i.delivery_date].filter(Boolean)).sort();
  const minDate = dates[0] || new Date().toISOString().slice(0, 10);
  const maxDate = dates[dates.length - 1] || minDate;
  const span = Math.max((new Date(maxDate) - new Date(minDate)) / 86400000, 1);
  const pos = (d) => Math.min(Math.max(((new Date(d) - new Date(minDate)) / 86400000 / span) * 100, 0), 100);

  return (
    <div className="space-y-4" data-testid="scm-lead-times">
      <div className="text-xs text-[var(--c-muted)]">
        Timeline per inquiry vs project delivery date. <span className="text-[var(--c-red)]">Red flag</span> = expected vendor delivery on/after project delivery (critical path).
      </div>
      <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm">
        <div className="px-4 py-2.5 border-b border-[var(--c-border)] flex justify-between text-[10px] font-mono text-[var(--c-muted)]">
          <span>{minDate}</span><span>{maxDate}</span>
        </div>
        {dated.map((i) => (
          <div key={i.id} className="px-4 py-3 border-b border-[var(--c-border)]/60 last:border-0" data-testid={`lead-row-${i.inquiry_id}`}>
            <div className="flex items-center gap-3 text-xs mb-1.5">
              <span className="font-mono text-[var(--c-accent)] w-44 shrink-0 truncate">{i.inquiry_id}</span>
              <span className="text-[var(--c-text)] flex-1 truncate">{i.component} · {i.project_code}</span>
              {i.critical_path && (
                <span className="inline-flex items-center gap-1 text-[10px] font-mono text-[var(--c-red)]" data-testid={`critical-${i.inquiry_id}`}>
                  <FlagBanner size={12} weight="fill" /> CRITICAL PATH
                </span>
              )}
              {i.overdue && <Warning size={13} weight="fill" className="text-[var(--c-red)]" />}
              <span className="font-mono text-[10px] text-[var(--c-muted)]">{i.days_remaining != null ? `${i.days_remaining}d left` : ""}</span>
            </div>
            <div className="relative h-2 bg-[var(--c-bg)] rounded-sm border border-[var(--c-border)]">
              {i.created_at && (
                <div className="absolute h-full rounded-sm bg-[var(--c-accent)]/50"
                  style={{ left: `${pos(i.created_at.slice(0, 10))}%`, width: `${Math.max(pos(i.expected_date) - pos(i.created_at.slice(0, 10)), 1.5)}%` }} />
              )}
              <div className="absolute w-2 h-2 -top-0 rounded-full bg-[var(--c-amber)]" style={{ left: `calc(${pos(i.expected_date)}% - 4px)` }}
                title={`Expected: ${i.expected_date}`} />
              {i.delivery_date && (
                <div className="absolute w-0.5 h-3.5 -top-1 bg-[var(--c-green)]" style={{ left: `${pos(i.delivery_date)}%` }}
                  title={`Project delivery: ${i.delivery_date}`} />
              )}
            </div>
            <div className="flex justify-between text-[10px] font-mono text-[var(--c-muted)] mt-1">
              <span>sent/created {String(i.created_at || "").slice(0, 10)}</span>
              <span>expected {i.expected_date}</span>
              {i.delivery_date && <span className="text-[var(--c-green)]">delivery {i.delivery_date}</span>}
            </div>
          </div>
        ))}
        {dated.length === 0 && (
          <div className="px-4 py-10 text-center text-sm text-[var(--c-muted)]" data-testid="lead-empty">
            No inquiries with expected dates yet. Send an inquiry to start tracking lead times.
          </div>
        )}
      </div>
    </div>
  );
}
